import { useInfiniteQuery, type QueryKey } from "@tanstack/react-query";
import { api, type ApiMeta } from "./api-client";

const PAGE_SIZE = 50;

/**
 * Tải danh sách theo trang, gộp lại thành một mảng phẳng kèm nút "tải thêm".
 *
 * Mọi danh sách trong hệ thống đều có thể lớn: một tài khoản Zalo thật đã có
 * gần 500 nhóm, lịch sử gửi thì tăng mãi. Tải hết một lần vừa nặng mạng vừa
 * làm trình duyệt dựng hàng nghìn phần tử DOM.
 */
export function usePagedQuery<T>(options: {
    queryKey: QueryKey;
    /** Đường dẫn kèm sẵn tham số lọc, KHÔNG có page/pageSize */
    path: string;
    enabled?: boolean;
    pageSize?: number;
    refetchInterval?: number;
}) {
    const pageSize = options.pageSize ?? PAGE_SIZE;

    const query = useInfiniteQuery({
        queryKey: [...options.queryKey, pageSize],
        enabled: options.enabled ?? true,
        refetchInterval: options.refetchInterval,
        initialPageParam: 1,
        queryFn: ({ pageParam }) => {
            const separator = options.path.includes("?") ? "&" : "?";
            return api.getWithMeta<T[]>(`${options.path}${separator}page=${pageParam}&pageSize=${pageSize}`);
        },
        getNextPageParam: (last: { data: T[]; meta?: ApiMeta }) => {
            const page = last.meta?.page ?? 1;
            const totalPages = last.meta?.totalPages ?? 1;
            return page < totalPages ? page + 1 : undefined;
        },
    });

    const items = query.data?.pages.flatMap((page) => page.data) ?? [];
    const total = query.data?.pages[0]?.meta?.total ?? items.length;

    return {
        items,
        total,
        isPending: query.isPending,
        error: query.error,
        hasMore: query.hasNextPage,
        isLoadingMore: query.isFetchingNextPage,
        loadMore: () => void query.fetchNextPage(),
        refetch: query.refetch,
    };
}
